import {
  CategoryType,
  Mission,
  useTradeMission,
  useMissionDetail,
} from '@/app/hooks/trade/tradeMission';
import { useMemo, useState } from 'react';
import SubMissionTab from '../../SubMissionTab';
import { getMissionTemplateCode, MissionCode } from '../../utils';
import Description from './Description';
import * as S from './styles';
import RewardLoading from '../RewardLoading';
import { useTranslation } from 'react-i18next';

// Local assets
import tradePlanetsImg from '@/assets/images/reward-page/trade-planets.svg';
import trophyVector1Img from '@/assets/images/reward-page/trophy-vector-1.svg';
import trophyVector2Img from '@/assets/images/reward-page/trophy-vector-2.svg';
import trophyVector3Img from '@/assets/images/reward-page/trophy-vector-3.svg';
import trophyVector4Img from '@/assets/images/reward-page/trophy-vector-4.svg';
import trophyVector5Img from '@/assets/images/reward-page/trophy-vector-5.svg';
import trophyVector6Img from '@/assets/images/reward-page/trophy-vector-6.svg';
import trophyVector7Img from '@/assets/images/reward-page/trophy-vector-7.svg';
import trophyVector8Img from '@/assets/images/reward-page/trophy-vector-8.svg';
import trophyVector9Img from '@/assets/images/reward-page/trophy-vector-9.svg';
import trophyVector10Img from '@/assets/images/reward-page/trophy-vector-10.svg';
import trophyVector11Img from '@/assets/images/reward-page/trophy-vector-11.svg';
import trophyVector12Img from '@/assets/images/reward-page/trophy-vector-12.svg';
import trophyVector13Img from '@/assets/images/reward-page/trophy-vector-13.svg';
import coin1Img from '@/assets/images/reward-page/coin-1.svg';
import coin2Img from '@/assets/images/reward-page/coin-2.svg';
import coin3Img from '@/assets/images/reward-page/coin-3.svg';
import coin4Img from '@/assets/images/reward-page/coin-4.svg';

export enum SubMission {
  DEPOSIT = 'Cumulative Net Deposit Mission',
  TRADING_VOLUME = 'Cumulative Trading Volume Mission',
  DURATION = 'Duration Mission',
}

const subMissions = [
  SubMission.DEPOSIT,
  SubMission.TRADING_VOLUME,
  SubMission.DURATION,
];

const TradeDetails = () => {
  const { t } = useTranslation();
  const [currentSubMission, setCurrentSubMission] = useState<SubMission>(
    subMissions[0]
  );

  const { data, getMissionReward, isLoading } =
    useTradeMission(CategoryType.TRADING); // 카테고리별 API 호출
  const tradingMissions = data?.missions ?? []; // 이미 필터링된 데이터

  const depositMissions = useMemo(
    () =>
      tradingMissions.filter(
        (m) => getMissionTemplateCode(m.code) === MissionCode.NET_DEPOSIT_GOAL
      ),
    [tradingMissions]
  );

  const volumeMissions = useMemo(
    () =>
      tradingMissions.filter(
        (m) => getMissionTemplateCode(m.code) === MissionCode.NET_VOLUME_GOAL
      ),
    [tradingMissions]
  );

  const periodMissions = useMemo(
    () =>
      tradingMissions.filter(
        (m) => getMissionTemplateCode(m.code) === MissionCode.PERIOD_MISSION
      ),
    [tradingMissions]
  );

  // useSWR로 미션 상세 조회 (3초 캐싱)
  const { data: detailDepositMission } = useMissionDetail(
    depositMissions[0]?.id
  );
  const { data: detailVolumeMission } = useMissionDetail(volumeMissions[0]?.id);
  const { data: detailPeriodMission } = useMissionDetail(periodMissions[0]?.id);

  // 추후 조건문에 기간미션 추가해야함.
  if (
    isLoading ||
    !depositMissions ||
    !volumeMissions ||
    !detailDepositMission ||
    !detailVolumeMission
  ) {
    return <RewardLoading messageKey="loadingTradingMission" />;
  }

  return (
    <S.SectionWrapper>
      {/* Background */}
      <S.Background />
      
      {/* Planets Background */}
      <S.PlanetsBg>
        <img src={tradePlanetsImg} alt="planets" />
      </S.PlanetsBg>

      {/* Trophy Vector Group (Left) */}
      <S.TrophyVectorGroup>
        <S.TrophyVector1 src={trophyVector1Img} alt="vector" />
        <S.TrophyVector2 src={trophyVector2Img} alt="vector" />
        <S.TrophyVector3 src={trophyVector3Img} alt="vector" />
        <S.TrophyVector4 src={trophyVector4Img} alt="vector" />
        <S.TrophyVector5 src={trophyVector5Img} alt="vector" />
        <S.TrophyVector6 src={trophyVector6Img} alt="vector" />
        <S.TrophyVector7 src={trophyVector7Img} alt="vector" />
        <S.TrophyVector8 src={trophyVector8Img} alt="vector" />
        <S.TrophyVector9 src={trophyVector9Img} alt="vector" />
        <S.TrophyVector10 src={trophyVector10Img} alt="vector" />
        <S.TrophyVector11 src={trophyVector11Img} alt="vector" />
        <S.TrophyVector12 src={trophyVector12Img} alt="vector" />
        <S.TrophyVector13 src={trophyVector13Img} alt="vector" />
        <S.TrophyNumber>1</S.TrophyNumber>
      </S.TrophyVectorGroup>

      {/* Coin Images */}
      <S.Coin1>
        <img src={coin1Img} alt="coin" />
      </S.Coin1>

      <S.Coin2>
        <img src={coin2Img} alt="coin" />
      </S.Coin2>

      <S.Coin3>
        <img src={coin3Img} alt="coin" />
      </S.Coin3>

      <S.Coin4>
        <img src={coin4Img} alt="coin" />
      </S.Coin4>

      {/* Content */}
      <S.ContentContainer>
        <S.SectionTitle>Deposit & Trading Volume Rewards</S.SectionTitle>
        
        <S.Container>
          <SubMissionTab
            currentSubMission={currentSubMission}
            setCurrentSubMission={setCurrentSubMission}
            subMissions={subMissions}
          />

          <Description
            currentSubMission={currentSubMission}
            depositMission={depositMissions[0]}
            volumeMission={volumeMissions[0]}
            periodMission={periodMissions[0]}
            detailDepositMission={detailDepositMission}
            detailVolumeMission={detailVolumeMission}
            detailPeriodMission={detailPeriodMission}
            getMissionReward={getMissionReward}
          />
        </S.Container>
      </S.ContentContainer>
    </S.SectionWrapper>
  );
};

export default TradeDetails;
